/**
 * S5 교사 API(C-07 · Bearer · role teacher · 자기 코스만 — 계약: 스크래치 s5-api.md '교사').
 *   GET/PUT  /api/courses/{c}/teacher/settings                (422 {code:"HTTP-422", field})
 *   GET      /api/courses/{c}/teacher/students                → {items:[TeacherStudent]}
 *   PUT      /api/courses/{c}/teacher/nicknames/{pid} {nickname, confirmSuspect?}  (409 {realNameSuspect:true})
 *   DELETE   /api/courses/{c}/teacher/nicknames/{pid}         → 204(자동 별명으로)
 *   GET      /api/courses/{c}/teacher/works                   → {items:[TeacherWorkItem]}(별명 순)
 *   PATCH    /api/courses/{c}/teacher/works/{snapshotId} {titleOverride?, teacherTags?, confirmSuspect?}
 *   GET      /api/modes/recommend?tags=a,b                    → {items:[{modeId, presetKey, weight}]}
 * 교사 입력은 셸에서 자유 텍스트가 허용되는 유일한 곳(별명 · 작품명) — 학생 화면에는 입력이 없다(DR-02).
 * 정렬 컨트롤 없음(SR-SOC-02): 목록은 서버 순서(별명 순)를 그대로 쓴다.
 */
import { useMemo } from "react";
import type { Appearance } from "@picnic-field/renderer/appearance";
import { ApiError } from "../../../lib/http/errors";
import type { LessonRef, Visibility } from "../../../lib/api/fieldApi";
import { useSession, useSessionManager } from "../../../store/session/SessionProvider";
import type { Requester } from "../../../types/api";
import type {
  CourseSettings,
  TeacherStudent,
  TeacherWorkItem,
  WorkPatch,
  Recommendation,
} from "../types";

/** 실명 의심(409) — 교사가 확인하면 confirmSuspect 로 다시 보낸다 */
export class RealNameSuspect extends Error {
  constructor() {
    super("real-name-suspect");
    this.name = "RealNameSuspect";
  }
}

/** 422 입력 검증 실패 — field 는 서버가 가리킨 칸 */
export class InvalidField extends Error {
  constructor(readonly field: string | null) {
    super(`invalid ${field ?? ""}`);
    this.name = "InvalidField";
  }
}

export interface TeacherApi {
  settings(): Promise<CourseSettings>;
  saveSettings(s: Omit<CourseSettings, "updatedAt">): Promise<CourseSettings>;
  students(): Promise<TeacherStudent[]>;
  setNickname(
    pid: string,
    nickname: string,
    confirmSuspect?: boolean,
  ): Promise<{ nickname: string; realNameSuspect: boolean }>;
  clearNickname(pid: string): Promise<void>;
  works(): Promise<TeacherWorkItem[]>;
  patchWork(snapshotId: string, patch: WorkPatch): Promise<TeacherWorkItem>;
  recommend(tags: readonly string[]): Promise<Recommendation[]>;
}

export const TITLE_MAX = 20;
export const NICKNAME_MAX = 20;
export const TIME_MIN_S = 300;
export const TIME_MAX_S = 1200;

const listOf = <T>(v: unknown, key = "items"): T[] => {
  if (Array.isArray(v)) return v as T[];
  const o = (v ?? {}) as Record<string, unknown>;
  return Array.isArray(o[key]) ? (o[key] as T[]) : [];
};
const str = (v: unknown, d = ""): string => (typeof v === "string" ? v : d);
const strs = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];

const VIS: readonly Visibility[] = ["class", "selected", "private"];

export function readSettings(v: unknown): CourseSettings {
  const o = (v ?? {}) as Record<string, unknown>;
  const t = Number(o.timeBudgetS);
  return {
    // DB visibility_t 'course' ↔ 셸 "class"(계약) — 옛 값도 너그럽게 받는다
    visibility:
      o.visibility === "course" ? "class" : (VIS.find((x) => x === o.visibility) ?? "class"),
    selected: strs(o.selected),
    presetKey: typeof o.presetKey === "string" ? o.presetKey : null,
    timeBudgetS: Number.isFinite(t) && t > 0 ? t : 600,
    realism: o.realism === true,
    updatedAt: typeof o.updatedAt === "string" ? o.updatedAt : null,
  };
}

export function readWork(v: unknown): TeacherWorkItem {
  const o = (v ?? {}) as Record<string, unknown>;
  const autoTags = strs(o.autoTags);
  const teacherTags = Array.isArray(o.teacherTags) ? strs(o.teacherTags) : null;
  const autoTitle = str(o.autoTitle, str(o.title));
  const titleOverride = typeof o.titleOverride === "string" ? o.titleOverride : null;
  const counts: Record<string, number> = {};
  for (const [k, n] of Object.entries((o.reactionCounts ?? {}) as Record<string, unknown>))
    if (typeof n === "number" && n > 0) counts[k] = n;
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  return {
    snapshotId: str(o.snapshotId),
    pid: str(o.pid),
    nickname: str(o.nickname),
    title: str(o.title, titleOverride ?? autoTitle),
    autoTitle,
    titleOverride,
    tags: Array.isArray(o.tags) ? strs(o.tags) : (teacherTags ?? autoTags),
    autoTags,
    teacherTags,
    reactionCounts: counts,
    reactionTotal: typeof o.reactionTotal === "number" ? o.reactionTotal : total,
    modelSlug: str(o.modelSlug),
    glbUrl: typeof o.glbUrl === "string" ? o.glbUrl : null,
    appearance: (o.appearance as Appearance | null) ?? null,
    static: o.static === true,
    lesson: (o.lesson as LessonRef | null) ?? null,
    createdAt: str(o.createdAt),
  };
}

function readStudent(v: unknown): TeacherStudent {
  const o = (v ?? {}) as Record<string, unknown>;
  return {
    pid: str(o.pid),
    nickname: str(o.nickname),
    autoNickname: str(o.autoNickname, str(o.nickname)),
    nicknameSet: o.nicknameSet === true,
    hasWork: o.hasWork === true,
    selected: o.selected === true,
    realNameSuspect: o.realNameSuspect === true,
  };
}

/** 409 실명 의심 · 422 검증 실패를 셸 오류로 바꾼다 */
function translate(e: unknown): never {
  if (e instanceof ApiError) {
    const b = (e.body ?? {}) as { realNameSuspect?: unknown; field?: unknown };
    if (e.status === 409 && b.realNameSuspect === true) throw new RealNameSuspect();
    if (e.status === 422) throw new InvalidField(typeof b.field === "string" ? b.field : null);
  }
  throw e;
}

export function createTeacherApi(request: Requester, courseId: string): TeacherApi {
  const c = `/api/courses/${encodeURIComponent(courseId)}/teacher`;
  const json = (method: string, body: unknown): RequestInit => ({
    method,
    body: JSON.stringify(body),
  });
  return {
    settings: async () => readSettings(await request<unknown>(`${c}/settings`)),
    async saveSettings(s) {
      const body = {
        visibility: s.visibility,
        selected: s.visibility === "selected" ? s.selected : [],
        presetKey: s.presetKey,
        timeBudgetS: s.timeBudgetS,
        realism: s.realism,
      };
      try {
        return readSettings(await request<unknown>(`${c}/settings`, json("PUT", body)));
      } catch (e) {
        return translate(e);
      }
    },
    students: async () => listOf(await request<unknown>(`${c}/students`)).map(readStudent),
    async setNickname(pid, nickname, confirmSuspect) {
      try {
        const v = await request<{ nickname?: unknown; realNameSuspect?: unknown }>(
          `${c}/nicknames/${encodeURIComponent(pid)}`,
          json("PUT", confirmSuspect ? { nickname, confirmSuspect: true } : { nickname }),
        );
        return {
          nickname: str(v?.nickname, nickname),
          realNameSuspect: v?.realNameSuspect === true,
        };
      } catch (e) {
        return translate(e);
      }
    },
    async clearNickname(pid) {
      await request(`${c}/nicknames/${encodeURIComponent(pid)}`, { method: "DELETE" });
    },
    works: async () => listOf(await request<unknown>(`${c}/works`)).map(readWork),
    async patchWork(id, patch) {
      try {
        return readWork(
          await request<unknown>(`${c}/works/${encodeURIComponent(id)}`, json("PATCH", patch)),
        );
      } catch (e) {
        return translate(e);
      }
    },
    async recommend(tags) {
      if (tags.length === 0) return [];
      try {
        const v = await request<unknown>(
          `/api/modes/recommend?tags=${tags.map(encodeURIComponent).join(",")}`,
        );
        return listOf<Record<string, unknown>>(v)
          .map((r) => ({
            modeId: str(r.modeId),
            presetKey: typeof r.presetKey === "string" ? r.presetKey : null,
            weight: typeof r.weight === "number" ? r.weight : 0,
          }))
          .filter((r) => r.modeId);
      } catch {
        return []; // 추천은 보조 정보 — 실패해도 설정은 된다
      }
    },
  };
}

/** 현재 세션 코스의 교사 API(교사 세션이 아니면 null) */
export function useTeacherApi(): TeacherApi | null {
  const manager = useSessionManager();
  const state = useSession();
  const courseId =
    state.status === "ready" && state.session.role === "teacher" ? state.session.courseId : null;
  return useMemo(
    () => (courseId ? createTeacherApi(manager.request, courseId) : null),
    [manager, courseId],
  );
}
